import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NbDialogService } from '@nebular/theme';
import { cloneDeep, isNil, isNumber } from 'lodash';
import { ChannelSelectionComponent } from '../../../../shared/components/modals/channel-selection/channel-selection.component';
import { ENUM_THEMES } from '../../../../shared/helpers/app.constants';
import { Helper } from '../../../../shared/helpers/helper';
import { TimerState } from '../../../../shared/helpers/intervalTimer';
import { IChannelItem, IChannelMappingResponse, IChannelSettingItemModel, IChannelSettingModel, IInputDataChart, IInputDataMultiFlow } from '../../../../shared/models';
import { IPlotTemplateResponse } from '../../../../shared/models/plot-template.model';
import { ChannelSelectionService, DIGITS_AFTER_DECIMAL_DEFAULT } from '../../../../shared/services/channel-selection.service';
import { ThemesSettingsService } from '../../../../shared/services/themes-settings.service';
import { UnitSystemService } from '../../../../shared/services/unit-system.service';
import { IRealtimeDataStateSetting } from '../realtime-treatment/realtime-treatment.component';


@Component({
  selector: 'app-treatment-data-panel',
  templateUrl: './treatment-data-panel.component.html',
  styleUrls: ['./treatment-data-panel.component.scss']
})
export class TreatmentDataPanelComponent implements OnInit, OnDestroy {
  channelSetting: IChannelSettingModel;
  channelItemSettings: IChannelSettingItemModel[] = [];
  dataPanelItems: IChannelSettingItemModel[] = [];
  chartInputData: IInputDataMultiFlow;
  selectedTemplate: IPlotTemplateResponse;
  defaultBreakLength = 12;

  @Input() isLoadingChartData: boolean;
  @Input()
  listRealtimeChannels: IChannelItem[] = [];
  @Input()
  listFracProChannels: IChannelItem[] = [];
  @Input()
  wellId: number = 0;
  @Input()
  treatmentId: number = 0;
  @Input()
  flowPathType: number = 0;
  @Input()
  dataPlotTemplates: IPlotTemplateResponse[];
  @Input()
  channelMappingData: IChannelMappingResponse[] = [];
  @Input()
  fracProChannelsLatestVer: any[] = [];

  @Output()
  channelSettingChange: EventEmitter<IChannelSettingModel> = new EventEmitter();
  @Output()
  componentClose: EventEmitter<boolean> = new EventEmitter();
  @Output()
  componentTransform: EventEmitter<boolean> = new EventEmitter();
  @Output() timerStateChange: EventEmitter<TimerState> = new EventEmitter();
  _dialogOpened: boolean = false;
  @Input('dialogOpened')
  set dialogOpened(value: boolean) {
    if (this._dialogOpened !== value) {
      this._dialogOpened = !!value;
      this.dialogOpenedChange.emit(this.dialogOpened);
    }
  }
  get dialogOpened(): boolean {
    return this._dialogOpened;
  }
  @Output() dialogOpenedChange: EventEmitter<boolean> = new EventEmitter();

  digitsAfterDecimalOptions: string[] = ['0', '1', '2', '3', '4'];
  // digitsDecimal: string = '1.0-2';
  digitsAfterDecimalValue: string = '2';
  digitsAfterDecimals: {templateId: number, digitsAfterDecimal: number}[] = [];

  get digitsDecimal(): string {
    return `1.0-${this.digitsAfterDecimalValue}`;
  }

  // @Output() digitsAfterDecimalChange: EventEmitter<{templateId: number, digitsAfterDecimal: number}> = new EventEmitter();

  isDarkTheme: boolean;

  constructor(
    private dialogService: NbDialogService,
    private channelSelectionService: ChannelSelectionService,
    private themesSettingsService: ThemesSettingsService,
    private unitSystemService: UnitSystemService
  ) {
    const localData = this.themesSettingsService.getLocalStorage();
    if (localData && localData.themes === ENUM_THEMES.Dark) {
      this.isDarkTheme = true;
    } else {
      this.isDarkTheme = false;
    }
  }

  private onChangeTimerState(state: number): void {
    if (!state) this.timerStateChange.emit(TimerState.idle);
    else this.timerStateChange.emit(state);
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
  }

  @Input() set setDataStateSetting(setting: IRealtimeDataStateSetting) {
    this.channelSetting = setting.channelSetting;
    this.channelItemSettings = this.channelSelectionService.getListChannels(setting.channelSetting);
    this.selectedTemplate = this.channelSelectionService.findPlotTemplate(setting.channelSetting, this.dataPlotTemplates);
  }

  @Input()
  set setChartInputData(data: IInputDataMultiFlow) {
    this.chartInputData = data;
    this.dataPanelItems = this.getDataPanelItemsMulti(this.channelItemSettings, data);
  }

  private getDataPanelItemsMulti(channelSettings: IChannelSettingItemModel[], inputData: IInputDataMultiFlow): IChannelSettingItemModel[] {
    if (!inputData) return channelSettings;

    const mapData = (data: IInputDataChart[]) => {
      if (!data || !data[0]) return;
      const inputCNames = data[0].columns;
      const inputValues = data[0].values;
      const firstData = inputValues && inputValues[0] ? inputValues[0] : null;
      const lastData = inputValues && inputValues[0] ? inputValues[inputValues.length - 1] : null;
      if (lastData) {
        for (const channel of channelSettings) {
          const findIndex = inputCNames.indexOf(channel.cName);
          if (findIndex < 0) continue;
          if (channel.digits === null || channel.digits === undefined) channel.digits = DIGITS_AFTER_DECIMAL_DEFAULT;
          channel.digitsFormat = `1.${channel.digits}-${channel.digits}`;
          channel.channelValue = lastData[findIndex];
          channel.lastData = lastData;
          channel.firstData = firstData;
        }
      }
    };

    mapData(inputData.fracproData);
    mapData(inputData.realtimeData);
    return channelSettings;
  }

  isNumber = (value) => isNumber(value);

  private get simpleChannelSetting(): IChannelSettingModel {
    const defaultChannelSettingYAxisModel = { min: null, max: null, channels: [] };
    const simpleChannelSettingYAxisModel = { min: null, max: null, channels: this.channelItemSettings };
    return {
      autoScaleYAxis: true,
      firstLeft: simpleChannelSettingYAxisModel,
      secondLeft: cloneDeep(defaultChannelSettingYAxisModel),
      firstRight: cloneDeep(defaultChannelSettingYAxisModel),
      secondRight: cloneDeep(defaultChannelSettingYAxisModel)
    };
  }

  private compareChannelSetting(originChannelSettings: IChannelSettingItemModel[], channelSettings: IChannelSettingItemModel[]) {
    if (originChannelSettings.length !== channelSettings.length) return false;
    for (const originSetting of originChannelSettings) {
      const find = channelSettings.find(x => x.name === originSetting.name && x.channelName === originSetting.channelName);
      if (!find) return false;
    }
    for (const setting of channelSettings) {
      const find = originChannelSettings.find(x => x.name === setting.name && x.channelName === setting.channelName);
      if (!find) return false;
    }
    return true;
  }

  private resetChannelSetting(channelItemSettings: IChannelSettingItemModel[]) {
    this.channelSetting.firstLeft.channels = [];
    this.channelSetting.secondLeft.channels = [];
    this.channelSetting.firstRight.channels = [];
    this.channelSetting.secondRight.channels = [];

    const split = Helper.splitNumber(channelItemSettings.length, 4, true);
    for (let i = 0; i < channelItemSettings.length; i++) {
      if (i >= split[3]) {
        this.channelSetting.secondRight.channels.push(channelItemSettings[i]);
      }
      else if (i >= split[2]) {
        this.channelSetting.firstRight.channels.push(channelItemSettings[i]);
      }
      else if (i >= split[1]) {
        this.channelSetting.secondLeft.channels.push(channelItemSettings[i]);
      }
      else if (i >= split[0]) {
        this.channelSetting.firstLeft.channels.push(channelItemSettings[i]);
      }
    }

    this.dataPanelItems = this.getDataPanelItemsMulti(this.channelItemSettings, this.chartInputData);
    this.channelSettingChange.emit(this.channelSetting);
  }

  onOpenChannelSelection() {
    this.dialogOpened = true;
    this.onChangeTimerState(TimerState.paused);
    this.dialogService.open(ChannelSelectionComponent, {
      context: {
        title: 'Channel Selection',
        realtimeChannels: this.listRealtimeChannels,
        fracProChannels: this.channelSelectionService.handleChannelsColor(this.listFracProChannels, this.fracProChannelsLatestVer),
        settings: this.simpleChannelSetting,
        wellId: this.wellId,
        treatmentId: this.treatmentId,
        flowPathType: this.flowPathType,
        simpleMode: true,
        maxChannelSelect: 0
      }
    }).onClose.subscribe(res => {
      this.dialogOpened = false;
      this.onChangeTimerState(TimerState.resumed);
      if (!res) return;
      const channelSetting = this.channelSelectionService.formatChannelSettingModel(res);
      this.channelItemSettings = channelSetting.firstLeft.channels;
      this.resetChannelSetting(channelSetting.firstLeft.channels);
    });
  }

  onSelectPlotTemplate(plotTemplate: IPlotTemplateResponse) {
    if (!plotTemplate || !plotTemplate.content) return;

    const template = plotTemplate.content;
    const autoScaleYAxis = this.channelSetting.autoScaleYAxis;
    const channelSetting = this.channelSelectionService.plotTemplateToChannelSettings(template, this.listRealtimeChannels, this.listFracProChannels, this.channelMappingData, autoScaleYAxis, undefined, this.fracProChannelsLatestVer);
    this.selectedTemplate = plotTemplate;
    // Check Channel Setting data
    const isChangeChannels = this.channelSelectionService.checkIsChangedChannels(channelSetting, this.channelSetting, true);
    if (!isChangeChannels) return;

    this.channelSetting = channelSetting;
    this.channelItemSettings = this.channelSelectionService.getListChannels(channelSetting);
    this.channelSettingChange.emit(this.channelSetting);
    this.dataPanelItems = this.getDataPanelItemsMulti(this.channelItemSettings, this.chartInputData);
  }

  onClose() {
    this.componentClose.emit(true);
  }

  onTransform() {
    this.componentTransform.emit(true);
  }

  getUnitConveter(measureType: string, curUnit: string): string {
    if (isNil(measureType) || !measureType.length) return curUnit ? `(${curUnit.trim()})` : '';
    else {
      const unit = this.unitSystemService.getUnit(measureType, undefined, curUnit);
      return unit ? `(${unit})` : '';
    }
  }

  convertUnit(measureType: string, value): number {
    if (isNil(measureType) || !measureType.length) return value;
    else return this.unitSystemService.convert(value, measureType);
  }

}
